import React, { useMemo, useState } from 'react';
import cx from 'classnames';

import { Contact, Recording, RecordingType } from '~/types/models';
import ActionLinks, { ActionLink } from './ActionLinks';
import QuickShareModal from './VideoShare/QuickShareModal';

import DeleteConfirmationModal from '~/App/Components/Modals/DeleteConfirmationModal';
import { useQueryClient } from 'react-query';
import {
  UpdateRecordingArgs,
  useUpdateRecording,
  useDeleteRecording,
  recordingsQueryKeys,
} from '../Api/Recordings';
import EditNameModal from '../../Testimonials/Components/EditNameModal';
import ThumbnailPickerModal from '../../Testimonials/Components/ThumbnailPicker/ThumbnailPickerModal';
import { ThumbnailArgs } from '~/App/Components/Record/Stores/Recorder/Thumbnail';
import { getThumbnailBlob } from '~/utils/thumbnail';
import ManageContactModal from '../../ManualUpload/Components/ManageContactModal';
import { useAssignResourceContact } from '../Api/ResourceContacts';
import { TaggableResourceType } from '~/types/models/tags';
import RemoveContactModal from './RemoveContactModal';
import { useRouteMatch } from 'react-router';
import { TestimonialAssetPage } from '~/types/models/testimonial';
import { ShareLibraryAssetTab } from '../../ShareLibrary/util';
import {
  EditableThumbnailResourcePage,
  EditableThumbnailResourceType,
} from '~/types/models/thumbnail';
import { userAssetsQueryKeys } from '../Api/UserAsset';

export interface RecordingActionLinksProps {
  recording: Recording;
  openManageContactModal: boolean;
  onRequestOpenManageContactModal: () => void;
  onRequestCloseManageContactModal: () => void;
  hideContactTagging?: boolean;
  showDownloadLink?: boolean;
}

export default function RecordingActionLinks({
  recording,
  openManageContactModal,
  onRequestOpenManageContactModal,
  onRequestCloseManageContactModal,
  hideContactTagging = false,
  showDownloadLink = false,
}: RecordingActionLinksProps) {
  const [showQuickShareModal, setShowQuickShareModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [showThumbnailPickerModal, setShowThumbnailPickerModal] =
    useState(false);
  const [showRemoveContactModal, setShowRemoveContactModal] = useState(false);

  const queryClient = useQueryClient();

  const closeRenameModal = () => setShowRenameModal(false);
  const closeThumbnailPickerModal = () => setShowThumbnailPickerModal(false);
  const closeDeleteConfirmationModal = () =>
    setShowDeleteConfirmationModal(false);
  const closeRemoveContactModal = () => setShowRemoveContactModal(false);

  const testimonialPage = useRouteMatch<{ assetPage?: TestimonialAssetPage }>();
  const { assetPage: testimonialLibraryPage } = testimonialPage.params;

  const match = useRouteMatch<{ tab?: ShareLibraryAssetTab }>();
  const { tab: shareLibraryAssetTab } = match.params;

  const { editableThumbnailResourceType, editableThumbnailResourcePage } =
    useMemo<{
      editableThumbnailResourcePage: string;
      editableThumbnailResourceType: string;
    }>(() => {
      const isTestimonial = recording.type === RecordingType.Testimonial;
      const editableThumbnailResourceType =
        recording.type === RecordingType.Upload || isTestimonial
          ? EditableThumbnailResourceType.Uploaded
          : EditableThumbnailResourceType.Recordings;

      let editableThumbnailResourcePage = '';

      if (isTestimonial) {
        if (testimonialLibraryPage === TestimonialAssetPage.All) {
          editableThumbnailResourcePage =
            EditableThumbnailResourcePage.TestimonialAll;
        } else {
          editableThumbnailResourcePage =
            EditableThumbnailResourcePage.TestimonialUploads;
        }
      } else {
        switch (shareLibraryAssetTab) {
          case ShareLibraryAssetTab.All:
            editableThumbnailResourcePage =
              EditableThumbnailResourcePage.ShareLibraryAll;
            break;
          case ShareLibraryAssetTab.Recordings:
            editableThumbnailResourcePage =
              EditableThumbnailResourcePage.ShareLibraryRecordings;
            break;
          case ShareLibraryAssetTab.Uploads:
          default:
            editableThumbnailResourcePage =
              EditableThumbnailResourcePage.ShareLibraryUploaded;
            break;
        }
      }
      return { editableThumbnailResourcePage, editableThumbnailResourceType };
    }, [recording, testimonialLibraryPage, shareLibraryAssetTab]);

  const invalidateQuery = () => {
    void queryClient.invalidateQueries(userAssetsQueryKeys.all);
    void queryClient.invalidateQueries(recordingsQueryKeys.lists());
  };

  const { mutateAsync: updateRecordingMutation, isLoading: updating } =
    useUpdateRecording({
      onSuccess: () => {
        closeRenameModal();
        closeThumbnailPickerModal();
        return invalidateQuery();
      },
    });

  const { mutateAsync: deleteRecordingMutation, isLoading: deleting } =
    useDeleteRecording({
      onSuccess: () => {
        closeDeleteConfirmationModal();
        return invalidateQuery();
      },
    });

  const { mutateAsync: assignResourceContactMutation, isLoading: isAssigning } =
    useAssignResourceContact({
      options: {
        onSuccess: () => {
          onRequestCloseManageContactModal();
          return invalidateQuery();
        },
      },
    });

  const handleOnDelete = () => {
    void deleteRecordingMutation(recording);
  };

  const handleOnRename = (name: string) => {
    void updateRecordingMutation({ id: recording.id, name });
  };

  const handleOnThumbnailSelected = async (thumbnail: ThumbnailArgs) => {
    const thumbnailBlob = await getThumbnailBlob(thumbnail.original);
    const args: UpdateRecordingArgs = {
      id: recording.id,
      thumbnail: thumbnailBlob,
    };
    await updateRecordingMutation(args);
  };

  const handleOnContactAssign = (contact: Contact) => {
    void assignResourceContactMutation({
      profileUuid: contact.id,
      resourceType: TaggableResourceType.UserAsset,
      resourceUuid: recording.id,
    });
  };

  const handleOnContactSave = () => {
    onRequestCloseManageContactModal();
    void invalidateQuery();
  };

  const defaultLinkClass = cx('hover:!v2-bg-yellow-100');

  const defaultLinkLabelClass = cx('v2-bg-neutral-900');

  const deleteButtonClass = cx('v2-text-orange-700');

  const deleteButtonContainerClass = cx('hover:!v2-bg-magenta-50');

  const links = useMemo<ActionLink[]>(() => {
    const hasContact = recording.author !== null;

    const contactActionLinks: ActionLink[] = [];

    if (!hideContactTagging) {
      contactActionLinks.push({
        label: (
          <>
            <i
              className={cx('icon icon-sm', defaultLinkLabelClass, {
                'icon-add-contact': !hasContact,
                'icon-edit-contact': hasContact,
              })}
            />{' '}
            <span className='text-sm'>
              {hasContact ? 'Edit profile' : 'Add a profile'}
            </span>
          </>
        ),
        onClick: onRequestOpenManageContactModal,
        className: defaultLinkClass,
      });

      if (hasContact) {
        contactActionLinks.push({
          label: (
            <>
              <i
                className={cx(
                  'icon icon-sm icon-remove-contact',
                  defaultLinkLabelClass
                )}
              />{' '}
              <span className='text-sm'>Remove profile</span>
            </>
          ),
          onClick: () => setShowRemoveContactModal(true),
          className: defaultLinkClass,
        });
      }
    }

    const downloadActionLinks: ActionLink[] = [];

    if (showDownloadLink) {
      downloadActionLinks.push({
        label: (
          <>
            <i
              className={cx(
                'icon icon-sm icon-download',
                defaultLinkLabelClass
              )}
            />{' '}
            <span className='text-sm'>Download</span>
          </>
        ),
        onClick: () =>
          window.open(recording.video_url, '_blank', 'noopener,noreferrer'),
        className: defaultLinkClass,
      });
    }

    return [
      {
        label: (
          <>
            <i className='far fa-share-alt' />{' '}
            <span className='text-sm'>Share video</span>
          </>
        ),
        onClick: () => setShowQuickShareModal(true),
        className: defaultLinkClass,
      },
      ...contactActionLinks,
      {
        label: (
          <>
            <i className='fa fa-image' />{' '}
            <span className='text-sm'>Edit thumbnail</span>
          </>
        ),
        onClick: () => setShowThumbnailPickerModal(true),
        className: defaultLinkClass,
      },
      {
        label: (
          <>
            <i
              className={cx('icon icon-sm icon-text', defaultLinkLabelClass)}
            />{' '}
            <span className='text-sm'>Rename</span>
          </>
        ),
        onClick: () => setShowRenameModal(true),
        className: defaultLinkClass,
      },
      ...downloadActionLinks,
      {
        label: (
          <>
            <i className={`fa fa-trash ${deleteButtonClass}`} />{' '}
            <span className={`text-sm  ${deleteButtonClass}`}>Delete</span>
          </>
        ),
        onClick: () => setShowDeleteConfirmationModal(true),
        className: deleteButtonContainerClass,
      },
    ];
  }, [recording]);

  return (
    <>
      <ActionLinks links={links} />
      <QuickShareModal
        show={showQuickShareModal}
        onModalClose={() => setShowQuickShareModal(false)}
        recording={recording}
      />
      <ThumbnailPickerModal
        open={showThumbnailPickerModal}
        onClose={closeThumbnailPickerModal}
        videoUrl={recording.video_url}
        onSave={handleOnThumbnailSelected}
        saving={updating}
        resourcePage={editableThumbnailResourcePage}
        resourceType={editableThumbnailResourceType}
      />
      <EditNameModal
        heading='Name your video'
        open={showRenameModal}
        onRequestClose={closeRenameModal}
        onConfirmed={handleOnRename}
        defaultValue={recording.name}
        loading={updating}
        confirmButtonText='Rename'
        showCancelButton
      />
      <DeleteConfirmationModal
        open={showDeleteConfirmationModal}
        title={`Are you sure you want to delete ${
          recording.name ?? 'this recording'
        }?`}
        onConfirm={handleOnDelete}
        onCancel={closeDeleteConfirmationModal}
        loading={deleting}
      />
      <ManageContactModal
        open={openManageContactModal}
        onRequestClose={onRequestCloseManageContactModal}
        initialValue={recording.author}
        onContactSave={handleOnContactSave}
        onContactAssign={handleOnContactAssign}
        loading={isAssigning}
      />
      <RemoveContactModal
        open={showRemoveContactModal}
        profileUuid={recording.author?.id as string}
        resourceUuid={recording.id}
        resourceType={TaggableResourceType.UserAsset}
        onModalClose={closeRemoveContactModal}
        onSuccess={invalidateQuery}
      />
    </>
  );
}
